/*
 * This is using a class component because at the time this is being written
 * functional component error boundaries are not support.  This could be
 * converted when that is corrected by the React team:
 * https://legacy.reactjs.org/docs/hooks-faq.html#do-hooks-cover-all-use-cases-for-classes
 *
 * Do not use Prizeout components in this module. If one of them has an error, this will not
 * display properly and defeat the purpose of having an error boundary. In fact, it's important
 * not to use Prizeout infrastructure - including CSS - where possible.  This needs to be as
 * vanilla React as possible while still looking like Prizeout code.
 *
 * Usage:
 *  <ErrorBoundary fallback={renderErrorBoundaryFallback}>
 *     <SomeReactComponent />
 *  </ErrorBoundary>
 *
 *  Note that this should be used only on React components.  Native JSX errors can't be
 *  caught at the JSX level.  They will only be caught if they occur inside of a React
 *  component when that component's use is wrapped with the ErrorBoundary.
 *
 *  ErrorBoundaries can be nested and the closest one containing the error will respond.
 *
 *  Note that the `environment` prop should not be used outside of a test environment.
 */

import React from 'react';
import { FallbackRender } from '@sentry/react';
import classNames from 'classnames';
import { isProduction } from '../helpers/environment';

import './error-boundary.scss';

export const ErrorBoundaryFallBack: React.FC<{ error: unknown; componentStack: string; eventId: string }> = ({
    error,
    componentStack,
    eventId,
}: {
    error: Error;
    componentStack: string;
    eventId: string;
}): React.ReactElement => {
    // MRiehle: Using `useState` here is causing this fallback to crash itself. This is not the time to
    //          debug that. Setting it up to just always show the error and component stacks until we
    //          can address https://prizeout.atlassian.net/browse/WV2-3035
    const showErrorStack = true;
    const showComponentStack = true;

    const nopOnClick = () => {
        return;
    };

    /* For CSS purposes, treat this as a separate component. This is
     * again part of avoiding too much dependency on Prizeout infrastructure.
     */
    const renderStackListItem = (statusFlag: boolean, stackName: string, stack: string, onClick: () => void) => {
        const listItemClassNames = classNames('stack-list-item', {
            'stack-list-item--show': statusFlag,
        });

        return (
            <li className={listItemClassNames}>
                <p className="stack-list-item__title" onClick={onClick.bind(this)}>
                    {stackName}
                </p>
                <div className="stack-list-item__stack">
                    <pre>{stack}</pre>
                </div>
            </li>
        );
    };

    const renderStackList = (error: Error, componentStack: string) => {
        if (isProduction()) {
            return;
        }

        return (
            <ul className="error-boundary__stack-list" data-testid="error-boundary-stack-list">
                {renderStackListItem(
                    showErrorStack,
                    'Error Stack',
                    error.stack,
                    () => nopOnClick /*toggleShowErrorStack*/,
                )}
                {renderStackListItem(
                    showComponentStack,
                    'Component Stack',
                    componentStack,
                    nopOnClick /*toggleShowComponentStack*/,
                )}
            </ul>
        );
    };

    const renderErrorDescription = (error: Error) => {
        if (isProduction()) {
            return (
                <p className="error-boundary__error-message" data-testid="customer-message">
                    We seem to be having a problem. Please try again later or contact Prizeout suport
                </p>
            );
        }

        return (
            <>
                <p className="error-boundary__error-name" data-testid="raw-message-name">
                    {error.name}
                </p>
                <p className="error-boundary__error-message" data-testid="raw-message-message">
                    {error.message}
                </p>
            </>
        );
    };

    return (
        <section className="error-boundary" data-testid="error-boundary">
            <article>
                <h1 className="error-boundary__title">There was a problem (Event ID: {eventId})</h1>
                <div className="error-boundary__details">
                    {renderErrorDescription(error)}
                    {renderStackList(error, componentStack)}
                </div>
            </article>
        </section>
    );
};

export const renderErrorBoundaryFallback: FallbackRender = ({
    error,
    componentStack,
    eventId,
}: {
    error: unknown;
    componentStack: string;
    eventId: string;
}) => {
    return <ErrorBoundaryFallBack error={error} componentStack={componentStack} eventId={eventId} />;
};
